import { Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpErrorResponse, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http';
import { Observable, EMPTY, throwError, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';

import { MatSnackBar } from '@angular/material/snack-bar';

import { environment } from '@env/environment';
import { Logger } from '../logger.service';

const log = new Logger('ErrorHandlerInterceptor');

/**
 * Adds a default error handler to all requests.
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerInterceptor implements HttpInterceptor {
  private router: Router | null = null;

  constructor(
    private _snackBar: MatSnackBar,
    private injector: Injector
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    //console.log('ErrorHandlerInterceptor');
    return next.handle(request).pipe(catchError((error) => this.handleError(error, request)));
  }

  private handleError(error: HttpErrorResponse, request?: HttpRequest<any>) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(`Backend returned code ${error.status}, ` + `body was: ${error.error}`);
      console.error('Full error object:', error);
      console.error('Error headers:', error.headers);
      console.error('Error URL:', error.url);

      // Handle 401 Unauthorized - redirect to login if not already there
      if (error.status === 401) {
        // Don't redirect if it's the refresh endpoint itself (to avoid loops)
        const isRefreshEndpoint = request?.url?.includes('/auth/refresh');
        
        if (!isRefreshEndpoint) {
          // Lazy inject Router to avoid circular dependency
          if (!this.router) {
            this.router = this.injector.get(Router);
          }
          
          // Only redirect if not already on login page
          if (this.router && !this.router.url.includes('/login')) {
            console.log('ErrorHandlerInterceptor: Redirecting to login due to 401 error');
            this.router.navigate(['/login'], {
              queryParams: { 
                redirect: this.router.url 
              },
              replaceUrl: false
            });
          }
        }
        
        this._snackBar.open(
          'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
          'Schließen',
          {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            duration: 5000,
          }
        );
      } else {
        // error.status == 504
        if (error.status < 200 || error.status > 350) {
          // Skip showing generic error for verification code errors (handled in component)
          const isVerificationError = request?.url?.includes('/auth/verify-email-code');
          
          if (isVerificationError && error.error && error.error.message) {
            // Don't show snackbar for verification errors - component handles it
            // Just log it
            console.log('Verification code error (handled in component):', error.error.message);
          } else if (error.error && error.error.error == true) {
            // custom error message
            this._snackBar.open('' + error.error.message, 'Schließen', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              duration: 3000,
            });
          } else if (error.error && error.error.error) {
            // Handle error object with error property
            this._snackBar.open('' + error.error.error, 'Schließen', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              duration: 3000,
            });
          } else {
            /*|| error.status != 'undefined' */
            this._snackBar.open(
              'Die Internet-Verbindung ist nicht stabil. Sie können weiterarbeiten, doch kann es zu Verzögerungen kommen.',
              'Schließen',
              {
                horizontalPosition: 'center',
                verticalPosition: 'top',
                duration: 3000,
              }
            );
          }
        }
      }
    }
    // Return an observable with a user-facing error message.
    // For debugging, pass through the original error to see backend details
    return throwError(error);
  }

  /*
  // Customize the default error handler here if needed
  private errorHandler(error: HttpEvent<any>): Observable<HttpEvent<any>> {

    if (error instanceof Error) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(`Backend returned code ${error}, body was: ${error}`);
    }

    // If you want to return a new response:
    //return of(new HttpResponse({body: [{name: "Default value..."}]}));

    // If you want to return the error on the upper level:
    //return throwError(error);

    // or just return nothing:
    return EMPTY;

    if (!environment.production) {
      // Do something with the error
      log.error('Request error', error);
    }
    throw error;
  }
  */
}
